import { awardForPlace, chartPlaceCount, type PointsTableRow } from './definition';
import { trimNumber } from './wording';

/**
 * §9.3's picture: points by finishing place.
 *
 * A grid of from/to/points rows is precise and says nothing about *shape* — whether a table is
 * top-heavy, whether the drop from 9th to 10th is a cliff, whether the tail is flat. That is the
 * thing an operator is actually deciding, and it reads off a bar chart instantly.
 *
 * Two details that keep it honest. It draws the ladder the table describes, so a place matching
 * no row draws as nothing — which is what it scores. And where the bottom row runs to the last
 * paid place, the chart cannot know how deep the money went: it draws a little past where the
 * table stops discriminating and fades the tail, rather than picking a number and implying it.
 */
export function PlacementChart({
  rows,
  className = '',
}: {
  rows: readonly PointsTableRow[];
  className?: string;
}) {
  const places = chartPlaceCount(rows);
  if (places === 0) return null;

  const awards = Array.from({ length: places }, (_, i) => awardForPlace(rows, i + 1));
  const peak = Math.max(...awards, 0);
  if (peak <= 0) return null;

  // Where the table stops saying anything new. Everything past it is the same award repeating,
  // and is drawn as a continuation rather than as data.
  const lastRow = rows[rows.length - 1];
  const openFrom = lastRow && lastRow.to === null ? (lastRow.from ?? places + 1) : places + 1;

  // Ticks thin out as the table deepens: a label under all 64 bars is a grey smear.
  const tickEvery = places <= 12 ? 1 : places <= 30 ? 5 : 10;

  return (
    <figure className={`min-w-0 ${className}`}>
      <div
        role="img"
        aria-label={describe(rows)}
        className="flex h-28 items-end gap-px border-b border-line"
      >
        {awards.map((award, i) => {
          const place = i + 1;
          const open = place >= openFrom;
          return (
            <span
              key={place}
              // The bar is the value; `title` gives the exact figure on hover for the places the
              // axis has no room to label.
              title={`${ordinal(place)}${open ? ' and below' : ''}: ${trimNumber(award)}`}
              className={`min-w-0 flex-1 ${open ? 'bg-felt-700/35' : 'bg-felt-700'}`}
              style={{ height: `${Math.max((award / peak) * 100, award > 0 ? 2 : 0)}%` }}
            />
          );
        })}
      </div>

      <div className="mt-1 flex gap-px" aria-hidden>
        {awards.map((_, i) => {
          const place = i + 1;
          const show = place === 1 || place % tickEvery === 0;
          return (
            <span
              key={place}
              className="num min-w-0 flex-1 text-center text-[9px] leading-none text-ink-800/40"
            >
              {show ? place : ''}
            </span>
          );
        })}
      </div>

      <figcaption className="mt-2 flex flex-wrap justify-between gap-x-4 gap-y-1 text-[11px] text-ink-800/50">
        <span>Points by finishing place</span>
        <span className="num">
          {trimNumber(peak)} at the top
          {openFrom <= places ? ` · faded from ${ordinal(openFrom)}, which runs to the last paid place` : ''}
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * The chart in words, for a screen reader. Reads the *table*, not the 64 bars — the shape is the
 * information, and "100, 70, 50, 44…" one bar at a time is not a shape.
 */
function describe(rows: readonly PointsTableRow[]): string {
  const parts = rows
    .filter((row) => row.from !== null && row.points !== null)
    .map((row) => {
      const from = ordinal(row.from as number);
      const points = trimNumber(row.points as number);
      if (row.to === null) return `${points} from ${from} to the last paid place`;
      if (row.to === row.from) return `${points} for ${from}`;
      return `${points} for ${from} to ${ordinal(row.to)}`;
    });

  return parts.length === 0
    ? 'Points by finishing place: nothing set yet.'
    : `Points by finishing place: ${parts.join('; ')}.`;
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;

  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
